/**
 *
 * @module mediaApi
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/MediaApi.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import MediaManager from './mediaManager';
import MediaDescriptor from './mediaDescriptor';
import MediaItem from './mediaItem';
import PlayerAdapter from './playerAdapter';
import ThumbnailResolution from './thumbnailResolution';

const Util = Services.Util;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const MediaApiDustUrnReference = Services.Internal.Dust.DustUrnReference.media.mediaApi;
const Presentation = Services.Media.Presentation;

const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    instanceStrict,
    number,
    includes
} = Check.assert;

/**
 *
 * @class MediaApi
 * @desc Provides a resource that can be used to help load, control, and monitor media playback.
 *
 */
export default class MediaApi {

    /**
     *
     * @param {MediaManager} mediaManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(mediaManager, logger = Logging) {

        const errorMessage = {
            ctor: 'MediaApi(mediaManager, logger)',
            get mediaManager() {
                return `${this.ctor} needs MediaManager instance`;
            }
        };

        instanceStrict(mediaManager, MediaManager, errorMessage.mediaManager, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {MediaManager}
         *
         */
        this.mediaManager = mediaManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.mediaManager.client.dustEnabled);

        this.logger.info(MediaApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {PlayerAdapter} playerAdapter - The player adapter you would like to create a new media session for.
     * @param {Array<SDK.Services.Media.DrmType>} [priorityDrms=null] - override DRM with a specified DRM or order
     * of DRMs
     * @desc Creates a new instance of PlaybackSession initialized with the supplied playerAdapter.
     * This is the entry point into all further interactions with media related SDK features.
     * @returns {Promise<PlaybackSession>} Injected with all dependencies.
     * @example mediaApi.createPlaybackSession(playerAdapter).then((playbackSession) => {});
     *
     */
    createPlaybackSession(playerAdapter, priorityDrms = null) {

        const errorMessage = {
            method: `${this.constructor.name}.createPlaybackSession(playerAdapter)`,
            get playerAdapter() {
                return `${this.method} needs PlayerAdapter instance`;
            },
            get priorityDrms() {
                return `${this.method} needs an array of SDK.Service.Media.DrmType`;
            }
        };

        const { dustEnabled, logger, mediaManager } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaApi.name,
            urn: MediaApiDustUrnReference.createPlaybackSession
        });

        instanceStrict(playerAdapter, PlayerAdapter, errorMessage.playerAdapter, InvalidArgumentException);

        if (Check.assigned(priorityDrms)) {
            array.of.nonEmptyString(priorityDrms, errorMessage.priorityDrms, InvalidArgumentException);
        }

        return mediaManager.createPlaybackSession(playerAdapter, priorityDrms).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {MediaDescriptor} mediaDescriptor - Supplies the media that should be
     * played and any relevant playback rules.
     * @since 3.3.0
     * @desc Fetches a media payload
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {AuthorizationExpiredException} Could not reauthorize before attempting to access media.
     * @note When the playlist service request returns HTTP 401 with an error of access-token.expired.
     * The only case where this could occur is when reauthorization failed due to a 5xx or network connection
     * issue and the token has expired. This implies an unstable network or service state and should be treated
     * like other unexpected errors.
     * @throws {LoginRequiredException} The requested media can only be viewed after authentication.
     * @note When the playlist service request returns HTTP 401 with an error of login.required.
     * @throws {NotEntitleException} The current user does not have sufficient privileges to access the media
     * for the requested license.
     * @note When the key service request returns HTTP 403 with an error of not-entitled.
     * @throws {BlackoutException} The current user is not allowed to access the media because they are in a
     * blackout location.
     * @note When the playlist service request returns HTTP 403 with an error of blackout.
     * @throws {ParentalControlsException} The current user is not allowed to access the media due to parental
     * controls restrictions on the account.
     * @note When the playlist service request returns HTTP 403 with an error of parental-control.
     * @throws {StreamConcurrencyException} The current user is not allowed to access the media for the requested
     * license because they have too many concurrent streams.
     * @note When the playlist service request returns HTTP 403 with an error of stream-concurrency-violation.
     * @throws {MediaNotAllowedException} The current user is not allowed to access the media.
     * @note When the playlist service request returns HTTP 403 with any error other than not-entitled, blackout,
     * parental-control, or stream-concurrency-violation.
     * @throws {MediaUnavailableException} The requested media is not available.
     * @note When the playlist service request returns HTTP 404 with an error of media.not-playable or media.missing.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Media.MediaItem>}
     *
     */
    fetch(mediaDescriptor) {

        const errorMessage = {
            method: `${this.constructor.name}.fetch(mediaDescriptor)`,
            get mediaDescriptor() {
                return `${this.method} needs MediaDescriptor instance`;
            }
        };

        const { dustEnabled, mediaManager, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaApi.name,
            urn: MediaApiDustUrnReference.fetch
        });

        instanceStrict(mediaDescriptor, MediaDescriptor, errorMessage.mediaDescriptor, InvalidArgumentException);

        return mediaManager.getMediaItem(mediaDescriptor).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Media.MediaItem} mediaItem - The MediaItem for the current media that was returned by a call to
     * Fetch.
     * @desc Fetches the details about the spritesheet thumbnail images that players can show to users when
     * scrubbing video.
     * @throws {ThumbnailsNotAvailableException} No thumbnails are available for the given media.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Array<SDK.Services.Media.SpriteThumbnailSet>>} A promise that returns a spritesheet
     * thumbnail set object describing the location and information about the thumbnails for all resolutions.
     *
     */
    getSpriteSheetThumbnailSets(mediaItem) {

        const errorMessage = {
            method: `${this.constructor.name}.getSpriteSheetThumbnailSets(mediaItem)`,
            get mediaItem() {
                return `${this.method} mediaItem should be an instance of SDK.Media.MediaItem`;
            }
        };

        instanceStrict(mediaItem, MediaItem, errorMessage.mediaItem, InvalidArgumentException);

        const spritesheet = Util.getSafe(() => mediaItem.payload.thumbnails.spritesheet);

        if (Check.not.nonEmptyObject(spritesheet)) {

            const thumbnailsNotAvailableException = new ServiceException({
                exceptionData: ExceptionReference.media.thumbnailsNotAvailable
            });

            return Promise.reject(thumbnailsNotAvailableException);

        }

        const { dustEnabled, mediaManager, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaApi.name,
            urn: MediaApiDustUrnReference.getSpriteSheetThumbnailSets,
            data: {
                thumbnailUrl: spritesheet.href
            }
        });

        return mediaManager.getSpriteSheetThumbnailSets(mediaItem).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Media.MediaItem} mediaItem - The MediaItem for the current media that was returned by a call to
     * Fetch.
     * @param {String} resolution - The desired resolution of thumbnails to fetch
     * @desc Fetches the details about the spritesheet thumbnail images that players can show to users when
     * scrubbing video.
     * @throws {ThumbnailsNotAvailableException} No thumbnails are available for the given media.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Media.SpriteThumbnailSet>} A promise that returns a spritesheet thumbnail set
     * object describing the location and information about the thumbnails at the given resolution.
     *
     */
    getSpriteSheetThumbnailSet(mediaItem, resolution) {

        const errorMessage = {
            method: `${this.constructor.name}.getSpriteSheetThumbnailSet(mediaItem, resolution)`,
            get mediaItem() {
                return `${this.method} mediaItem should be an instance of SDK.Media.MediaItem`;
            },
            get resolution() {
                return `${this.method} resolution should be a member of SDK.Media.ThumbnailResolution`;
            }
        };

        instanceStrict(mediaItem, MediaItem, errorMessage.mediaItem, InvalidArgumentException);
        includes(ThumbnailResolution, resolution, errorMessage.resolution, InvalidArgumentException);

        const spritesheet = Util.getSafe(() => mediaItem.payload.thumbnails.spritesheet);

        if (Check.not.nonEmptyObject(spritesheet)) {

            const thumbnailsNotAvailableException = new ServiceException({
                exceptionData: ExceptionReference.media.thumbnailsNotAvailable
            });

            return Promise.reject(thumbnailsNotAvailableException);

        }

        const { dustEnabled, mediaManager, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaApi.name,
            urn: MediaApiDustUrnReference.getSpriteSheetThumbnailSet,
            data: {
                thumbnailUrl: spritesheet.href,
                thumbnailResolution: resolution
            }
        });

        return mediaManager.getSpriteSheetThumbnailSet(mediaItem, resolution).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Services.Media.Presentation} presentation - The thumbnail presentation to download. Typically
     * chosen by desired resolution.
     * @param {Number} index - The index of the thumbnail in the pod to download.
     * @desc Fetches the details about the spritesheet thumbnail images that players can show to users when scrubbing
     * video.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Array<Byte>>} Fetches an actual spritesheet thumbnail image that players can show to users
     * when scrubbing video.
     *
     */
    getSpriteSheetThumbnail(presentation, index) {

        const errorMessage = {
            method: `${this.constructor.name}.getSpriteSheetThumbnail(presentation, index)`,
            get presentation() {
                return `${this.method} presentation should be an instance of SDK.Services.Media.Presentation`;
            },
            get index() {
                return `${this.method} index should be a Number`;
            }
        };

        instanceStrict(presentation, Presentation, errorMessage.presentation, InvalidArgumentException);
        number(index, errorMessage.index, InvalidArgumentException);

        const { dustEnabled, mediaManager, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaApi.name,
            urn: MediaApiDustUrnReference.getSpriteSheetThumbnail,
            data: {
                spriteSheetUrl: presentation.paths[index]
            }
        });

        return mediaManager.getSpriteSheetThumbnail(presentation, index).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Media.MediaItem} mediaItem - The MediaItem for the current media that was returned by a call to
     * Fetch.
     * @desc Fetches the details about the BIF thumbnail images that players can show to users when scrubbing video.
     * @throws {ThumbnailsNotAvailableException} No thumbnails are available for the given media.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Array<SDK.Services.Media.BifThumbnailSet>>} A promise that returns a BIF thumbnail set object
     * describing the location and information about the thumbnails for all resolutions.
     *
     */
    getBifThumbnailSets(mediaItem) {

        const errorMessage = {
            method: `${this.constructor.name}.getBifThumbnailSets(mediaItem)`,
            get mediaItem() {
                return `${this.method} mediaItem should be an instance of SDK.Media.MediaItem`;
            }
        };

        instanceStrict(mediaItem, MediaItem, errorMessage.mediaItem, InvalidArgumentException);

        const bif = Util.getSafe(() => mediaItem.payload.thumbnails.bif);

        if (Check.not.nonEmptyObject(bif)) {

            const thumbnailsNotAvailableException = new ServiceException({
                exceptionData: ExceptionReference.media.thumbnailsNotAvailable
            });

            return Promise.reject(thumbnailsNotAvailableException);

        }

        const { dustEnabled, mediaManager, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaApi.name,
            urn: MediaApiDustUrnReference.getBifThumbnailSets,
            data: {
                thumbnailUrl: bif.href
            }
        });

        return mediaManager.getBifThumbnailSets(mediaItem).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Media.MediaItem} mediaItem - The MediaItem for the current media that was returned by a call to
     * Fetch.
     * @param {SDK.Media.ThumbnailResolution} resolution - The desired resolution of thumbnails to fetch.
     * @desc Fetches the details about the BIF thumbnail images that players can show to users when scrubbing video.
     * @throws {ThumbnailsNotAvailableException} No thumbnails are available for the given media.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Media.BifThumbnailSet>} A promise that returns a BIF thumbnail set object
     * describing the location and information about the thumbnails for the desired resolution.
     *
     */
    getBifThumbnailSet(mediaItem, resolution) {

        const errorMessage = {
            method: `${this.constructor.name}.getBifThumbnailSet(mediaItem, resolution)`,
            get mediaItem() {
                return `${this.method} mediaItem should be an instance of SDK.Media.MediaItem`;
            },
            get resolution() {
                return `${this.method} resolution should be a member of SDK.Media.ThumbnailResolution`;
            }
        };

        instanceStrict(mediaItem, MediaItem, errorMessage.mediaItem, InvalidArgumentException);
        includes(ThumbnailResolution, resolution, errorMessage.resolution, InvalidArgumentException);

        const bif = Util.getSafe(() => mediaItem.payload.thumbnails.bif);

        if (Check.not.nonEmptyObject(bif)) {

            const thumbnailsNotAvailableException = new ServiceException({
                exceptionData: ExceptionReference.media.thumbnailsNotAvailable
            });

            return Promise.reject(thumbnailsNotAvailableException);

        }

        const { dustEnabled, mediaManager, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaApi.name,
            urn: MediaApiDustUrnReference.getBifThumbnailSet,
            data: {
                thumbnailUrl: bif.href,
                thumbnailResolution: resolution
            }
        });

        return mediaManager.getBifThumbnailSet(mediaItem, resolution).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @note the JS SDK does not support this method
     * @returns {Promise<Void>}
     *
     */
    getBifThumbnail() {

        const errorMessage = {
            method: `${this.constructor.name}.getBifThumbnail()`
        };

        return new Promise((resolve, reject) => {
            return reject(new Error(`${errorMessage.method} - not-implemented`));
        });

    }

}



// WEBPACK FOOTER //
// ./src/media/mediaApi.js